import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Card, 
  Input, 
  Button, 
  Typography, 
  Space, 
  notification,
  Modal,
  Tag,
  InputNumber,
  Result,
  Spin,
  Row,
  Col,
  Empty,
  Divider
} from 'antd';
import { 
  SearchOutlined, 
  ShoppingCartOutlined,
  ScanOutlined,
  CheckOutlined,
  CloseOutlined,
  PlusOutlined,
  MinusOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import { database, ref, get } from '../pages/Auth/db/firebase';
import ResizableSlideover from '../components/grocery/ResizableSlideover';
import { GroceryCartProvider, useGroceryCart } from '../context/GroceryCartContext';
import ContinuousBarcodeScanner from '../components/checkout/ContinuousBarcodeScanner';
import GroceryReceipt from '../components/grocery/GroceryReceipt';
import { 
  getCurrentListId, 
  listenForCheckout, 
  clearListSession,
  registerListSession 
} from '../utils/groceryListSession';
import { formatPriceWithCurrency, getFacilityCurrency, preloadCustomCurrencies } from '../utils/currency';

const { Search } = Input;
const { Title, Text } = Typography;

// ========== CLIENT GROCERY SHOPPING PAGE ==========
const GroceryListInner = () => {
  const { facilityId } = useParams();
  const navigate = useNavigate();

  // Set page title
  useEffect(() => {
    document.title = `${facilityId} - Grocery List | KioStock`;
  }, [facilityId]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [, setListId] = useState(null);
  const [availableItems, setAvailableItems] = useState([]); // For barcode scanner
  const [allItems, setAllItems] = useState([]); // All items for display
  const [facilityCurrency, setFacilityCurrency] = useState('USD');
  const [productInfoModal, setProductInfoModal] = useState({ open: false, product: null, quantity: 1 });
  const [facilityExists, setFacilityExists] = useState(null); // null = checking, true = exists, false = doesn't exist
  const { addItem, clear, items, updateQuantity, removeItem } = useGroceryCart();
  const [api, contextHolder] = notification.useNotification();

  // ========== FACILITY VALIDATION ==========
  useEffect(() => {
    const checkFacility = async () => {
      if (!facilityId) {
        setFacilityExists(false);
        return;
      }

      try {
        // Check if facility has any data (inventory, cashiers, or users)
        const inventoryRef = ref(database, `inventory/${facilityId}`);
        const snapshot = await get(inventoryRef);
        
        if (snapshot.exists()) {
          setFacilityExists(true);
        } else {
          // Try case-insensitive match
          const allInventoryRef = ref(database, 'inventory');
          const allSnapshot = await get(allInventoryRef);
          
          if (allSnapshot.exists()) {
            const facilities = Object.keys(allSnapshot.val());
            const correctCase = facilities.find(f => f.toLowerCase() === facilityId.toLowerCase());
            
            if (correctCase && correctCase !== facilityId) {
              // Redirect to correct case
              navigate(`/${correctCase}`, { replace: true });
              return;
            }
          }
          
          setFacilityExists(false);
        }
      } catch (error) {
        console.error('Error checking facility:', error);
        setFacilityExists(false);
      }
    };

    checkFacility();
  }, [facilityId, navigate]);

  // ========== CHECKOUT NOTIFICATION LISTENER ==========
  // Initialize list ID and listen for checkout completion
  useEffect(() => {
    const currentListId = getCurrentListId(facilityId);
    setListId(currentListId);

    // Register session whenever cart changes
    if (items && items.length > 0) {
      registerListSession(facilityId, currentListId, items);
    }

    // Listen for checkout completion
    const unsubscribe = listenForCheckout(facilityId, currentListId, (receipt) => {
      setReceiptData(receipt);
      setReceiptOpen(true);
      clear(); // Clear the grocery cart
      clearListSession(facilityId, currentListId);
      
      // Generate new list ID for next shopping session
      const newListId = getCurrentListId(facilityId);
      setListId(newListId);
      
      api.success({
        message: 'Checkout Complete!',
        description: 'Your receipt is ready for download.',
        duration: 5
      });
    });

    return () => unsubscribe();
  }, [facilityId, items, clear, api]);

  // ========== LOAD FACILITY CURRENCY ==========
  useEffect(() => {
    if (!facilityId) return;
    
    const loadCurrency = async () => {
      try {
        const currency = await getFacilityCurrency(facilityId);
        setFacilityCurrency(currency);
        // Preload custom currencies for proper formatting
        await preloadCustomCurrencies(facilityId);
      } catch (error) {
        console.error('Error loading currency:', error);
      }
    };
    
    loadCurrency();
  }, [facilityId]);

  // ========== LOAD AVAILABLE ITEMS FOR BARCODE SCANNER AND DISPLAY ==========
  useEffect(() => {
    if (!facilityId) return;

    const loadItems = async () => {
      try {
        const itemsRef = ref(database, `inventory/${facilityId}/items`);
        const snapshot = await get(itemsRef);
        
        if (snapshot.exists()) {
          const itemsData = snapshot.val();
          const items = Object.entries(itemsData)
            .filter(([name, data]) => (data.quantity || 0) > 0) // Only show items in stock
            .map(([name, data]) => ({
              name,
              barcode: data.barcode,
              baseId: data.baseId,
              category: data.category,
              price: data.price || data.unitPrice || 0,
              currency: data.currency || facilityCurrency,
              stockQuantity: data.quantity || 0
            }));
          // For barcode scanner - need quantity field
          const scannerItems = items.map(item => ({
            ...item,
            quantity: 1
          }));
          setAvailableItems(scannerItems);
          setAllItems(items);
        } else {
          setAvailableItems([]);
          setAllItems([]);
        }
      } catch (error) {
        console.error('Error loading items:', error);
      }
    };

    loadItems();
  }, [facilityId, facilityCurrency]);

  const searchItem = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 3) {
      api.warning({
        message: 'Search Term Too Short',
        description: 'Please enter at least 3 characters or a complete product code.',
      });
      return;
    }
    setSearchLoading(true);
    try {
      const itemsRef = ref(database, `inventory/${facilityId}/items`);
      const snapshot = await get(itemsRef);
      if (snapshot.exists()) {
        const items = snapshot.val();
        const searchUpper = searchTerm.toUpperCase();
        let foundItem = null;
        for (const [name, data] of Object.entries(items)) {
          if (data.baseId === searchUpper || name.toUpperCase() === searchUpper) {
            foundItem = { name, ...data };
            break;
          }
        }
        if (foundItem) {
          // Show product info modal instead of adding directly
          setProductInfoModal({ open: true, product: foundItem, quantity: 1 });
        } else {
          api.error({ message: 'Item Not Found', description: `No item found with code or name: "${searchTerm}"` });
        }
      }
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    const onResize = () => {
      // Handle resize if needed
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Show loading while checking facility
  if (facilityExists === null) {
    return (
      <div className="public-page" style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Spin size="large" tip="Loading..." />
      </div>
    );
  }

  // Show error if facility doesn't exist
  if (facilityExists === false) {
    return (
      <div className="public-page" style={{ minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 'var(--spacing-xl)' }}>
        <Result
          status="404"
          title="Facility Not Found"
          subTitle={`The facility "${facilityId}" does not exist or has no inventory.`}
          extra={
            <Button type="primary" onClick={() => window.location.href = '/'}>
              Go to Login
            </Button>
          }
        />
      </div>
    );
  }

  // Group items by category
  const itemsByCategory = allItems.reduce((acc, item) => {
    const category = item.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  return (
    <div className="public-page" style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(to bottom, var(--bg-primary) 0%, var(--surface-elevated) 100%)',
      padding: 'var(--spacing-lg)',
      paddingBottom: items.length > 0 ? '400px' : 'var(--spacing-lg)'
    }}>
      {contextHolder}
      
      {/* Hero Section */}
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto',
        marginBottom: 'var(--spacing-xl)'
      }}>
        <div style={{ 
          textAlign: 'center', 
          padding: 'var(--spacing-xl) 0',
          marginBottom: 'var(--spacing-xl)'
        }}>
          <Title 
            style={{ 
              color: 'var(--text-primary)', 
              marginBottom: '16px',
              fontSize: '48px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, var(--primary-color) 0%, var(--accent-color) 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }} 
            level={1}
          >
            Welcome to {facilityId}
          </Title>
          <Text style={{ 
            fontSize: '20px', 
            color: 'var(--text-secondary)',
            display: 'block',
            marginBottom: '24px'
          }}>
            Discover our wide selection of quality products
          </Text>
          
          {/* Search Bar */}
          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <Space.Compact style={{ width: '100%' }}>
              <Search
                placeholder='Search products by name or code...'
                allowClear
                enterButton={<SearchOutlined />}
                size='large'
                loading={searchLoading}
                onSearch={searchItem}
                style={{ 
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
            </Space.Compact>
            <Space style={{ marginTop: '16px' }}>
              <Button 
                type='primary' 
                size='large'
                icon={<ScanOutlined />} 
                onClick={() => setScanOpen(true)}
                style={{
                  borderRadius: 'var(--radius-lg)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              >
                Scan Barcode
              </Button>
            </Space>
          </div>
        </div>

        {/* Products Grid */}
        {Object.keys(itemsByCategory).length > 0 ? (
          <div>
            {Object.entries(itemsByCategory).map(([category, categoryItems]) => (
              <div key={category} style={{ marginBottom: 'var(--spacing-xl)' }}>
                <Divider orientation="left">
                  <Title level={3} style={{ margin: 0, color: 'var(--text-primary)' }}>
                    {category}
                  </Title>
                </Divider>
                <Row gutter={[16, 16]}>
                  {categoryItems.map((item) => (
                    <Col xs={24} sm={12} md={8} lg={6} key={item.name}>
                      <Card
                        hoverable
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.setData('application/json', JSON.stringify({
                            name: item.name,
                            baseId: item.baseId,
                            price: item.price,
                            currency: item.currency || facilityCurrency,
                            category: item.category,
                            barcode: item.barcode
                          }));
                          e.dataTransfer.effectAllowed = 'copy';
                          // Add visual feedback
                          e.currentTarget.style.opacity = '0.5';
                          e.currentTarget.style.cursor = 'grabbing';
                          e.currentTarget.setAttribute('data-dragging', 'true');
                        }}
                        onDragEnd={(e) => {
                          e.currentTarget.style.opacity = '1';
                          e.currentTarget.style.cursor = 'grab';
                          e.currentTarget.removeAttribute('data-dragging');
                        }}
                        onTouchStart={(e) => {
                          // Mobile drag support
                          const touch = e.touches[0];
                          const card = e.currentTarget;
                          card.setAttribute('data-touch-start', JSON.stringify({
                            x: touch.clientX,
                            y: touch.clientY,
                            time: Date.now()
                          }));
                          card.style.opacity = '0.7';
                        }}
                        onTouchMove={(e) => {
                          // Prevent default scrolling while dragging
                          e.preventDefault();
                          const card = e.currentTarget;
                          const startData = card.getAttribute('data-touch-start');
                          if (startData) {
                            const start = JSON.parse(startData);
                            const touch = e.touches[0];
                            const deltaX = Math.abs(touch.clientX - start.x);
                            const deltaY = Math.abs(touch.clientY - start.y);
                            
                            // If moved more than 10px, consider it a drag
                            if (deltaX > 10 || deltaY > 10) {
                              card.setAttribute('data-dragging', 'true');
                              card.style.opacity = '0.5';
                            }
                          }
                        }}
                        onTouchEnd={(e) => {
                          const card = e.currentTarget;
                          const startData = card.getAttribute('data-touch-start');
                          
                          if (startData) {
                            const start = JSON.parse(startData);
                            const touch = e.changedTouches[0];
                            const deltaX = Math.abs(touch.clientX - start.x);
                            const deltaY = Math.abs(touch.clientY - start.y);
                            const timeDelta = Date.now() - start.time;
                            
                            // Check if it was a drag (moved more than 20px) or a tap
                            if ((deltaX > 20 || deltaY > 20) && timeDelta < 1000) {
                              const wasDragging = card.hasAttribute('data-dragging');
                              
                              // Check if cart slideover is open/visible
                              const cartDropZone = document.querySelector('[data-drop-zone]');
                              
                              // If we were dragging and cart is available, add the item
                              if (wasDragging && cartDropZone) {
                                const itemData = {
                                  name: item.name,
                                  baseId: item.baseId,
                                  price: item.price,
                                  currency: item.currency || facilityCurrency,
                                  category: item.category,
                                  barcode: item.barcode
                                };
                                const existingItem = items.find(i => i.name === item.name);
                                if (existingItem) {
                                  setProductInfoModal({ 
                                    open: true, 
                                    product: item, 
                                    quantity: 1,
                                    isDuplicate: true,
                                    existingItem 
                                  });
                                } else {
                                  addItem({ ...itemData, quantity: 1 });
                                  api.success({ 
                                    message: 'Item Added', 
                                    description: `${item.name} added to your list` 
                                  });
                                }
                              } else {
                                // Try the original method with elementFromPoint
                                let dropZone = null;
                                try {
                                  dropZone = document.elementFromPoint(touch.clientX, touch.clientY);
                                } catch (err) {
                                  console.error('Error getting element from point:', err);
                                }
                                
                                if (dropZone) {
                                  let target = dropZone;
                                  while (target && target !== document.body) {
                                    if (target.hasAttribute('data-drop-zone')) {
                                      const itemData = {
                                        name: item.name,
                                        baseId: item.baseId,
                                        price: item.price,
                                        currency: item.currency || facilityCurrency,
                                        category: item.category,
                                        barcode: item.barcode
                                      };
                                      const existingItem = items.find(i => i.name === item.name);
                                      if (existingItem) {
                                        setProductInfoModal({ 
                                          open: true, 
                                          product: item, 
                                          quantity: 1,
                                          isDuplicate: true,
                                          existingItem 
                                        });
                                      } else {
                                        addItem({ ...itemData, quantity: 1 });
                                        api.success({ 
                                          message: 'Item Added', 
                                          description: `${item.name} added to your list` 
                                        });
                                      }
                                      break;
                                    }
                                    target = target.parentElement;
                                  }
                                }
                              }
                            }
                          }
                          
                          card.style.opacity = '1';
                          card.removeAttribute('data-touch-start');
                          card.removeAttribute('data-dragging');
                        }}
                        style={{
                          height: '100%',
                          borderRadius: 'var(--radius-lg)',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          transition: 'all 0.3s ease',
                          border: '1px solid var(--border-secondary)',
                          cursor: 'grab',
                          touchAction: 'none' // Prevent default touch behaviors
                        }}
                        bodyStyle={{ padding: '16px' }}
                        onClick={(e) => {
                          // Only open modal if not dragging
                          if (!e.currentTarget.hasAttribute('data-dragging')) {
                            setProductInfoModal({ open: true, product: item, quantity: 1 });
                          }
                        }}
                        onMouseDown={(e) => {
                          // Mark as not dragging initially
                          e.currentTarget.removeAttribute('data-dragging');
                        }}
                      >
                        <div style={{ 
                          textAlign: 'center',
                          marginBottom: '12px'
                        }}>
                          <ShoppingOutlined style={{ 
                            fontSize: '48px', 
                            color: 'var(--primary-color)',
                            marginBottom: '8px'
                          }} />
                        </div>
                        <Title 
                          level={5} 
                          style={{ 
                            margin: '0 0 8px 0',
                            color: 'var(--text-primary)',
                            textAlign: 'center',
                            fontSize: '16px',
                            fontWeight: 600
                          }}
                          ellipsis
                        >
                          {item.name}
                        </Title>
                        {item.category && (
                          <div style={{ textAlign: 'center', marginBottom: '8px' }}>
                            <Tag color="blue">{item.category}</Tag>
                          </div>
                        )}
                        <div style={{ 
                          textAlign: 'center',
                          marginTop: '12px'
                        }}>
                          <Text strong style={{ 
                            fontSize: '20px', 
                            color: 'var(--primary-color)'
                          }}>
                            {formatPriceWithCurrency(item.price, item.currency || facilityCurrency, facilityId)}
                          </Text>
                          {item.stockQuantity !== undefined && (
                            <div style={{ marginTop: '4px' }}>
                              <Text type="secondary" style={{ fontSize: '12px' }}>
                                {item.stockQuantity > 0 ? (
                                  <span style={{ color: 'var(--success-color)' }}>
                                    {item.stockQuantity} in stock
                                  </span>
                                ) : (
                                  <span style={{ color: 'var(--error-color)' }}>
                                    Out of stock
                                  </span>
                                )}
                              </Text>
                            </div>
                          )}
                        </div>
                        <Button
                          type="primary"
                          block
                          style={{ marginTop: '12px' }}
                          icon={<PlusOutlined />}
                          onClick={(e) => {
                            e.stopPropagation();
                            addItem({ ...item, quantity: 1 });
                            api.success({ 
                              message: 'Item Added', 
                              description: `${item.name} added to your list` 
                            });
                          }}
                          disabled={!item.stockQuantity || item.stockQuantity === 0}
                        >
                          Add to List
                        </Button>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </div>
            ))}
          </div>
        ) : (
          <Card style={{ textAlign: 'center', padding: 'var(--spacing-xl)' }}>
            <Empty 
              description={
                <Text style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
                  No products available at the moment
                </Text>
              }
            />
          </Card>
        )}
      </div>

      {/* Grocery List - Collapsed by default at bottom */}
      <ResizableSlideover />
      
      {/* Continuous Barcode Scanner - Same as cashier mode */}
      <ContinuousBarcodeScanner
        visible={scanOpen}
        onClose={() => setScanOpen(false)}
        onCheckout={() => {
          setScanOpen(false);
          api.success({
            message: 'Items Added',
            description: 'Continue shopping or share your list with a cashier.'
          });
        }}
        availableItems={availableItems}
        cart={items.map(item => ({
          name: item.name,
          barcode: item.barcode || item.baseId,
          baseId: item.baseId,
          price: item.price || 0,
          currency: item.currency,
          quantity: item.quantity || 1
        }))}
        onAddToCart={(item, quantity) => {
          addItem({ ...item, quantity: quantity || 1 });
        }}
        onUpdateCart={(itemName, newQuantity) => {
          const key = items.find(i => i.name === itemName)?.key;
          if (key) updateQuantity(key, newQuantity);
        }}
        onRemoveFromCart={(itemName) => {
          const key = items.find(i => i.name === itemName)?.key;
          if (key) removeItem(key);
        }}
        facilityId={facilityId}
      />
      
      <GroceryReceipt
        open={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        receiptData={receiptData}
      />

      {/* Product Info Modal */}
      <Modal
        title={<Space><ShoppingCartOutlined /> Product Information</Space>}
        open={productInfoModal.open}
        onCancel={() => setProductInfoModal({ open: false, product: null, quantity: 1 })}
        footer={[
          <Button 
            key="cancel" 
            icon={<CloseOutlined />}
            onClick={() => setProductInfoModal({ open: false, product: null, quantity: 1 })}
          >
            Cancel
          </Button>,
          <Button
            key="add"
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => {
              if (productInfoModal.product) {
                const product = productInfoModal.product;
                const requestedQty = productInfoModal.quantity;
                const existingItem = items.find(i => i.name === product.name);
                const currentCartQty = existingItem ? existingItem.quantity : 0;
                const totalQty = currentCartQty + requestedQty;
                const stockQty = product.stockQuantity || 0;

                // Validate stock
                if (totalQty > stockQty) {
                  api.error({
                    message: 'Insufficient Stock',
                    description: `Only ${stockQty} ${product.name} available. You already have ${currentCartQty} in your cart.`
                  });
                  return;
                }

                // Add the specified quantity
                for (let i = 0; i < requestedQty; i++) {
                  addItem({ ...product, quantity: 1 });
                }
                
                api.success({ 
                  message: 'Item Added', 
                  description: `${requestedQty}x ${product.name} added to list` 
                });
                setProductInfoModal({ open: false, product: null, quantity: 1 });
              }
            }}
          >
            Add to List
          </Button>
        ]}
        width={500}
      >
        {productInfoModal.product && (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Typography.Text strong style={{ fontSize: '18px', color: 'var(--lcolor)' }}>
                {productInfoModal.product.name}
              </Typography.Text>
            </div>
            {productInfoModal.product.category && (
              <div>
                <Tag color="blue">{productInfoModal.product.category}</Tag>
              </div>
            )}
            {productInfoModal.product.barcode && (
              <div>
                <Typography.Text type="secondary" style={{ color: 'var(--text-secondary)' }}>Barcode: </Typography.Text>
                <Typography.Text code>{productInfoModal.product.barcode}</Typography.Text>
              </div>
            )}
            {productInfoModal.product.unitPrice !== undefined && (
                <div>
                  <Typography.Text type="secondary" style={{ color: 'var(--text-secondary)' }}>Price: </Typography.Text>
                  <Typography.Text strong style={{ fontSize: '16px', color: 'var(--primary-color)' }}>
                    {formatPriceWithCurrency(productInfoModal.product.unitPrice, productInfoModal.product.currency || facilityCurrency)}
                  </Typography.Text>
                </div>
            )}
            {productInfoModal.product.stockQuantity !== undefined && (
              <div>
                <Typography.Text type="secondary" style={{ color: 'var(--text-secondary)' }}>Available Stock: </Typography.Text>
                <Typography.Text strong style={{ color: productInfoModal.product.stockQuantity > 0 ? 'var(--success-color)' : 'var(--error-color)' }}>
                  {productInfoModal.product.stockQuantity} units
                </Typography.Text>
                {(() => {
                  const existingItem = items.find(i => i.name === productInfoModal.product.name);
                  const currentCartQty = existingItem ? existingItem.quantity : 0;
                  if (currentCartQty > 0) {
                    return (
                      <Typography.Text type="secondary" style={{ marginLeft: '8px', fontSize: '12px' }}>
                        ({currentCartQty} already in cart)
                      </Typography.Text>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
            <div>
              <Typography.Text type="secondary" style={{ color: 'var(--text-secondary)' }}>Quantity: </Typography.Text>
              <Space>
                <Button 
                  size="small"
                  icon={<MinusOutlined />}
                  onClick={() => setProductInfoModal(prev => ({ 
                    ...prev, 
                    quantity: Math.max(1, prev.quantity - 1) 
                  }))}
                  disabled={productInfoModal.quantity <= 1}
                />
                <InputNumber 
                  min={1} 
                  max={(() => {
                    const existingItem = items.find(i => i.name === productInfoModal.product.name);
                    const currentCartQty = existingItem ? existingItem.quantity : 0;
                    const stockQty = productInfoModal.product.stockQuantity || 0;
                    return Math.max(1, stockQty - currentCartQty);
                  })()}
                  value={productInfoModal.quantity}
                  onChange={(val) => {
                    const existingItem = items.find(i => i.name === productInfoModal.product.name);
                    const currentCartQty = existingItem ? existingItem.quantity : 0;
                    const stockQty = productInfoModal.product.stockQuantity || 0;
                    const maxAvailable = Math.max(1, stockQty - currentCartQty);
                    setProductInfoModal(prev => ({ 
                      ...prev, 
                      quantity: Math.min(val || 1, maxAvailable)
                    }));
                  }}
                  style={{ width: '80px' }}
                />
                <Button 
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    const existingItem = items.find(i => i.name === productInfoModal.product.name);
                    const currentCartQty = existingItem ? existingItem.quantity : 0;
                    const stockQty = productInfoModal.product.stockQuantity || 0;
                    const maxAvailable = stockQty - currentCartQty;
                    setProductInfoModal(prev => ({ 
                      ...prev, 
                      quantity: Math.min(prev.quantity + 1, maxAvailable)
                    }));
                  }}
                  disabled={(() => {
                    const existingItem = items.find(i => i.name === productInfoModal.product.name);
                    const currentCartQty = existingItem ? existingItem.quantity : 0;
                    const stockQty = productInfoModal.product.stockQuantity || 0;
                    return productInfoModal.quantity >= (stockQty - currentCartQty);
                  })()}
                />
              </Space>
            </div>
          </Space>
        )}
      </Modal>
    </div>
  );
};

const GroceryListPage = () => {
  const { facilityId } = useParams();
  return (
    <GroceryCartProvider facilityId={facilityId}>
      <GroceryListInner />
    </GroceryCartProvider>
  );
};

export default GroceryListPage;
